import * as THREE from 'three';
import type { HabitatCameraProfile, HostedScene } from './host';
import type { HourSun } from '../sunlight';
import type { Visible } from '../fishSchools';
import type { SpeciesVisitNote, Visitor, VisitorAt } from './visitors';
import type { MarkTarget, TagMarks } from '../tagMarks';
import { KEY_AT_FULL_SUN, WARM } from '../exhibitView';

/**
 * What every habitat scene is (M110): its own scene graph, camera and limits (`HostedScene`, drawn by the host), the
 * day's data from the adapter (`data.ts`) laid on it, the hour's light, its animals swum on the animation clock, its
 * level of detail fitted to the screen, the animal under a tap, and a measure of what its view is made of.
 */
export interface HabitatScene extends HostedScene {
  /** The habitat it shows, the whole tank's id (`Habitat['id']`). */
  readonly kind: string;
  /** The place it shows, by name, for the card and its note (the remembered views keyed by `OpenedHabitat.key`). */
  readonly locationName: string;
  /** The light at an hour of the day, the observation clock's. */
  setLight(sun: HourSun): void;
  /** Its animals where `hour` has them, swum on by `seconds` of the player's time, still within themselves when `calm`. */
  animate(hour: number, dayLengthHours: number, seconds: number, calm: boolean): void;
  /** Sized and thinned for the camera on a canvas `canvasPx` high, eased over `seconds`, measured over the part the panels leave. */
  fit(canvasPx: number, seconds: number, visible: Visible): void;
  /** The animal drawn nearest a point on the screen (normalized device coordinates), within `withinPx`; null for none. */
  pick(x: number, y: number, widthPx: number, heightPx: number, withinPx: number): HabitatPick | null;
  /** The day's visitor by its name (`visitorId`) as drawn now, a honu, a monk seal (M145) or a species' (M147), for the card that follows it (M152); null where none is. */
  visitorAt(id: string): VisitorAt | null;
  /** A visitor drawn handed over to another name, its place kept (M203: one tagged as it is followed, drawn as its tag's). */
  handOverVisitor(from: string, to: string, tag?: Visitor['tag']): void;
  /** A species' visitor of the day by its name, faded out, gone home past its slot's swap (M147); null for any other. */
  visitorGone(id: string): Visitor | null;
  /** A species' visit to it today from its own habitat (M147), as the panel tells it; null where it has none. */
  visitOf(kind: Visitor['kind']): SpeciesVisitNote | null;
  /** The tagged animals and named schools it marks (M156), and where a tag's mark stands in it; null where none of its kind is drawn. */
  readonly marks: TagMarks;
  markAt(target: MarkTarget, remembered: number | null): THREE.Vector3 | null;
  /** What its camera's view is made of, shares (`composition`). */
  composition(cols?: number, rows?: number): Composition;
}

/**
 * An animal tapped in a habitat scene: a school of reef fish by its seed (and on the Reef the fish tapped, by its name, M266), a whale by its place among those drawn, or the
 * day's visitor by its name (`visitorId`; M152, the user's word: "i should be able to select and follow every creature on every
 * screen").
 */
export type HabitatPick = { school: number; fish?: string } | { whale: number } | { visitor: string } | { octopus: number } | { bottomfish: string } | { dolphin: string };

/**
 * What a view is made of, shares of it: the floor; the coral and the rock standing on it; the water, as far as the eye
 * sees through it, and the surface's underside overhead.
 */
export interface Composition {
  floor: number;
  coral: number;
  water: number;
  surface: number;
}

/**
 * A view's composition, measured, not guessed (as the whole tank's `shotComposition`): rays through an even grid of
 * the camera's view, `cols` by `rows`, marched from the camera by `stepM` as far as `seenM` (where the water hides two
 * thirds of what lies beyond, `HabitatWater`'s fog: what lies farther reads as the water's own color); each counted as
 * coral where it first enters something standing on the floor (`standing`), the floor where it first goes under it,
 * the surface where it leaves the water by it, and water where it runs that far through the water.
 */
export function marchComposition(
  camera: THREE.PerspectiveCamera,
  floorY: (x: number, z: number) => number,
  standing: (p: THREE.Vector3) => boolean,
  surfaceY: number,
  seenM: number,
  stepM: number,
  cols = 30,
  rows = 50,
): Composition {
  camera.updateMatrixWorld();
  const counts = { floor: 0, coral: 0, water: 0, surface: 0 };
  const p = new THREE.Vector3();
  const ray = new THREE.Vector3();
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      ray.set(((i + 0.5) / cols) * 2 - 1, ((j + 0.5) / rows) * 2 - 1, 0.5).unproject(camera).sub(camera.position).normalize();
      let hit: keyof typeof counts = 'water';
      for (let t = stepM; t <= seenM; t += stepM) {
        p.copy(camera.position).addScaledVector(ray, t);
        if (p.y > surfaceY) { hit = 'surface'; break; }
        if (p.y <= floorY(p.x, p.z)) { hit = 'floor'; break; }
        if (standing(p)) { hit = 'coral'; break; }
      }
      counts[hit]++;
    }
  }
  const n = cols * rows;
  return { floor: counts.floor / n, coral: counts.coral / n, water: counts.water / n, surface: counts.surface / n };
}

/** The scene's lights: the sun's (or the moon's) key, and the water's light all round, from above, bluer below. */
export function habitatLights(): { key: THREE.DirectionalLight; fill: THREE.HemisphereLight } {
  return { key: new THREE.DirectionalLight(WARM.sunColor, KEY_AT_FULL_SUN), fill: new THREE.HemisphereLight(0xcfe8ff, 0x0a2030, 1.2) };
}

/**
 * The key light at an hour, as the whole tank's (`ExhibitView.setLight`): from the sun, at its beam's strength, warmer
 * near the horizon; by night from the moon by its phase and height, cool and dim; and the animals' tint at that light,
 * `seen` the light down here against a full sun's (`HabitatWater.seen`), warmer as the sun nears the horizon.
 */
export function lightAt(sun: HourSun, key: THREE.DirectionalLight, seen: number): [number, number, number] {
  const moon = sun.moon?.level ?? 0;
  const warm = sun.beam > 0 ? Math.min(1, Math.max(0, 1 - sun.elevationDeg / WARM.belowDeg)) : 0;
  if (sun.beam > 0 || !sun.moon || moon <= 0) {
    key.intensity = KEY_AT_FULL_SUN * sun.beam;
    key.position.set(...sun.direction);
    key.color.setHex(WARM.sunColor).lerp(new THREE.Color(WARM.color), warm);
  } else {
    key.intensity = KEY_AT_FULL_SUN * 0.15 * moon;
    key.position.set(...sun.moon.direction);
    key.color.setHex(0xc8d4ff);
  }
  return [seen, seen * (1 - WARM.tint[0] * warm), seen * (1 - WARM.tint[1] * warm)];
}

/** A camera profile's lens: wider upright, so a phone held upright sees across as much as a computer (JUDGED). */
export function habitatFov(landscape: number, portrait: number): (aspect: number) => number {
  return (aspect) => (aspect < 1 ? portrait : landscape);
}

export type { HabitatCameraProfile };
